import React from "react";
import * as R from "ramda";
import * as utils from "utils";
import { useSettings } from "utils/settingsContext";
import EmptyState from "components/EmptyState";
import { flows } from "components";
import { logError } from "api/accounts-customer";
import FlowStepsHandler from "./FlowStepsHandler";

// If the integrator is configured to inherit from the default,
// merge the appSettings flows with any overrides defined in the
// integratorSettings. Otherwise, only use the integratorSettings flows.
const inheritTrue = R.pipe(R.path(["flows", "inheritDefault"]), utils.isTrue);

const buildFlowMap = (appSettings, integratorSettings) => {
  return inheritTrue(integratorSettings)
    ? R.mergeDeepRight(appSettings.flows, integratorSettings.flows)
    : integratorSettings.flows;
};

const getFinalIntegratorFlowSteps = (appSettings, integratorSettings, flow, debug) => {
  const flowMap = buildFlowMap(appSettings, integratorSettings);
  debug("flowMap", flowMap);

  return R.pipe(
    R.prop(flow),
    utils.ensureArray,
    R.map((key) => R.defaultTo(EmptyState, R.prop(key, flows)))
  )(flowMap);
};

///----------///---------------------///----------

const FlowContainer = ({ flow }) => {
  const { sessionData, sessionJWT } = utils.useSessionJWT();
  const { debug, sessionCtx } = utils.useSessionCtx();
  const { postOnError } = utils.usePostMessage(flow, sessionCtx?.intCtx);
  const { appSettings, integratorSettings } = useSettings();
  debug("FLOW", flow);

  if (!utils.goodSessionData(sessionData)) {
    debug("Session data error", sessionData);
    postOnError({ message: "Session data error" });

    logError(sessionJWT, {
      context: "[ContainerPage] Bad Session Data",
      sessionData,
      flow,
      appSettings,
      integratorSettings,
    });

    return <EmptyState error title="Session data issue" />;
  }

  if (utils.isNilOrEmpty(flow)) {
    debug("Requested flow doesn't exist", flow);
    postOnError({ message: "Requested flow is missing" });

    logError(sessionJWT, {
      context: "[ContainerPage] Flow does not exist",
      flow,
      appSettings,
      integratorSettings,
    });

    return (
      <EmptyState
        title="Flow does not exist"
        description="The requested flow does not exist. Please contact support."
      />
    );
  }

  if (!R.equals(R.prop("enabled", integratorSettings), true)) {
    debug("Publisher has not been enabled", integratorSettings);
    postOnError({ message: "Publisher has not been enabled" });

    logError(sessionJWT, {
      context: "[ContainerPage] Publisher has not been enabled",
      integratorSettings,
    });

    return (
      <EmptyState
        title="Not enabled"
        description="The publisher associated with this request isn't enabled. Please contact support."
      />
    );
  }

  const flowSteps = getFinalIntegratorFlowSteps(appSettings, integratorSettings, flow, debug);

  return (
    <div
      data-testid={`container-page-${flow}`}
      style={{
        height: "100%",
      }}
    >
      <FlowStepsHandler config={integratorSettings} flow={flow} steps={flowSteps} />
    </div>
  );
};

export default FlowContainer;
